import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { designSystem } from '../../design-system';

// Aboutセクションのコンテナ
const AboutContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  backgroundColor: 'transparent',
  overflow: 'visible',
  zIndex: 1,
});

// Aboutセクション
const AboutSection = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '24px 120px',
  paddingBottom: '200px',
  zIndex: 1,
  overflow: 'visible',

  '@media (max-width: 1600px)': {
    padding: '24px 100px',
    paddingBottom: '180px',
  },

  '@media (max-width: 1440px)': {
    padding: '24px 80px',
    paddingBottom: '180px',
  },

  '@media (max-width: 1024px)': {
    padding: '24px 60px',
    paddingBottom: '160px',
  },

  '@media (max-width: 768px)': {
    padding: '24px 24px',
    paddingBottom: '140px',
  },
});

const AboutCard = styled(motion.div)({
  position: 'relative',
  width: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '16px',
  padding: '80px',
  display: 'flex',
  gap: '60px',
  alignItems: 'stretch',

  '@media (max-width: 1600px)': {
    padding: '70px',
    gap: '50px',
  },

  '@media (max-width: 1440px)': {
    padding: '56px',
    gap: '40px',
  },

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    padding: '48px',
    gap: '40px',
    alignItems: 'flex-start',
  },

  '@media (max-width: 768px)': {
    padding: '40px 32px',
    gap: '32px',
  },
});

const MessageSection = styled(Box)({
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  paddingLeft: '40px',

  '@media (max-width: 1600px)': {
    paddingLeft: '32px',
    gap: '28px',
  },

  '@media (max-width: 1440px)': {
    paddingLeft: '24px',
    gap: '24px',
  },

  '@media (max-width: 1200px)': {
    paddingLeft: 0,
    flex: '1 1 auto',
    gap: '32px',
  },
});

const Heading = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const Line = styled('div')({
  width: '40px',
  height: '2px',
  backgroundColor: '#B2CAD1',
  transform: 'rotate(180deg)',
});

const HeadingText = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '24px',
  fontWeight: 500,
  color: '#B2CAD1',
  letterSpacing: '0.24px',
  whiteSpace: 'nowrap',

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },
});

const TitleImageAbout = styled('img')({
  width: '100%',
  maxWidth: '490px',
  height: 'auto',

  '@media (max-width: 1600px)': {
    maxWidth: '420px',
  },

  '@media (max-width: 1440px)': {
    maxWidth: '380px',
  },

  '@media (max-width: 1200px)': {
    maxWidth: '400px',
  },

  '@media (max-width: 768px)': {
    maxWidth: '100%',
  },
});

const AboutText = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '24px',
  fontWeight: 400,
  color: '#6093A1',
  lineHeight: 1.5,
  letterSpacing: '0.24px',

  '@media (max-width: 1600px)': {
    fontSize: '21px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '18px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '20px',
  },

  '@media (max-width: 900px)': {
    fontSize: '18px',
  },

  '@media (max-width: 768px)': {
    fontSize: '16px',
  },
});

const ImageContainer = styled(Box)({
  flex: '0 1 auto',
  backgroundColor: 'rgba(156, 206, 218, 0.1)',
  borderRadius: '8px',
  overflow: 'hidden',
  maxWidth: '760px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',

  '@media (max-width: 1600px)': {
    maxWidth: '640px',
  },

  '@media (max-width: 1440px)': {
    maxWidth: '540px',
  },

  '@media (max-width: 1200px)': {
    maxWidth: '100%',
    width: '100%',
  },
});

const AboutImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',

  '@media (max-width: 1200px)': {
    maxWidth: '500px',
    margin: '0 auto',
  },

  '@media (max-width: 768px)': {
    maxWidth: '300px',
    margin: '0 auto',
  },

  '@media (max-width: 480px)': {
    maxWidth: '250px',
    margin: '0 auto',
  },
});

const HorizontalLoopContainer = styled(Box)({
  position: 'absolute',
  width: '100%',
  left: 0,
  right: 0,
  bottom: '40px',
  overflow: 'hidden',
  padding: '0',
  zIndex: 1,

  '@media (max-width: 1024px)': {
    bottom: '30px',
  },

  '@media (max-width: 768px)': {
    bottom: '20px',
  },
});

const LoopingText = styled(Box)({
  display: 'flex',
  animation: 'scroll 40s linear infinite',
  width: 'fit-content',

  '& img': {
    height: '110px',
    width: 'auto',
    objectFit: 'contain',
    display: 'block',
  },

  '@keyframes scroll': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' },
  },

  '@media (max-width: 768px)': {
    '& img': { height: '60px' },
  },
});


const AboutSectionComponent = () => {
  const [aboutRef, aboutVisible] = useScrollAnimation({ threshold: 0.2 });

  const cardVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.6, 0.01, 0.05, 0.95] },
    },
  };

  return (
    <AboutContainer id="about">
      {/* Aboutセクション */}
      <AboutSection ref={aboutRef}>
        <AboutCard
          variants={cardVariants}
          initial="hidden"
          animate={aboutVisible ? 'visible' : 'hidden'}
        >
          {/* 左側: メッセージセクション */}
          <MessageSection>
            <Heading>
              <Line />
              <HeadingText>ABOUT US</HeadingText>
            </Heading>

            <Box display="flex" flexDirection="column" gap="35px">
              <TitleImageAbout src="/title_about.png" alt="見過ごされている必要をかたちにする。" />
              
              <AboutText>
                STELLAは、テクノロジーとデザインの力で、<br />
                人や社会の営みに"本当に必要な光"を届ける会社です。<br />
                <br />
                見せるためではなく、届くために。<br />
                流行や効率ではなく、本質と誠実さを軸に。<br />
                <br />
                プロダクト、サービス、体験——<br />
                すべてのかたちに、やさしく確かな光を宿して。<br />
                <br />
                私たちは今日も、誰かの道を<br />
                静かに照らし続けています。
              </AboutText>
            </Box>
          </MessageSection>

          {/* 右側: About画像 */}
          <ImageContainer>
            <AboutImage src="/about_img.webp" alt="STELLA About" loading="lazy" />
          </ImageContainer>
        </AboutCard>
      </AboutSection>

      {/* 横スクロールテキスト */}
      <HorizontalLoopContainer>
        <LoopingText>
          <img src="/text_horizontal_loop.png" alt="To illuminate, not to shine • STELLA •" />
          <img src="/text_horizontal_loop.png" alt="To illuminate, not to shine • STELLA •" />
          <img src="/text_horizontal_loop.png" alt="To illuminate, not to shine • STELLA •" />
          <img src="/text_horizontal_loop.png" alt="To illuminate, not to shine • STELLA •" />
        </LoopingText>
      </HorizontalLoopContainer>
    </AboutContainer>
  );
};

export default AboutSectionComponent;

